import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

/**
 * OAuth 2.1 consent screen for the app's MCP server.
 *
 * Supabase Auth (the authorization server) redirects the user here as
 * /.lovable/oauth/consent?authorization_id=... so they can approve or deny a
 * connecting MCP client (Claude, ChatGPT, Cursor, ...).
 *
 * The route deliberately lives under a dot-directory: it is an auth surface,
 * not indexable content.
 */

type AuthorizationDetails = {
  client?: { name?: string; client_uri?: string } | null;
  redirect_url?: string;
  redirect_to?: string;
  scope?: string;
};

// `supabase.auth.oauth` is a beta namespace that is not in the published types
// yet. Wrap the three methods we use rather than reaching into internals.
type OAuthApi = {
  getAuthorizationDetails: (
    id: string,
  ) => Promise<{ data: AuthorizationDetails | null; error: { message: string } | null }>;
  approveAuthorization: (
    id: string,
  ) => Promise<{ data: AuthorizationDetails | null; error: { message: string } | null }>;
  denyAuthorization: (
    id: string,
  ) => Promise<{ data: AuthorizationDetails | null; error: { message: string } | null }>;
};

const oauthApi = () => (supabase.auth as unknown as { oauth?: OAuthApi }).oauth;

const SIGN_IN_PATH = "/.lovable/oauth/signin";

const OAuthConsent = () => {
  const [params] = useSearchParams();
  const authorizationId = params.get("authorization_id") ?? "";
  const [details, setDetails] = useState<AuthorizationDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;

    (async () => {
      if (!authorizationId) {
        setError("Missing authorization_id in the request URL.");
        return;
      }

      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        // Preserve the FULL consent URL so sign-in returns the user here and
        // the connector flow can complete instead of dropping them on "/".
        const next = window.location.pathname + window.location.search;
        window.location.href = `${SIGN_IN_PATH}?next=${encodeURIComponent(next)}`;
        return;
      }

      const api = oauthApi();
      if (!api) {
        setError(
          "The OAuth 2.1 authorization server is not enabled on this Supabase project yet.",
        );
        return;
      }

      const { data, error: detailsError } = await api.getAuthorizationDetails(authorizationId);
      if (!active) return;

      if (detailsError) {
        setError(detailsError.message);
        return;
      }

      const immediate = data?.redirect_url ?? data?.redirect_to;
      if (immediate && !data?.client) {
        window.location.href = immediate;
        return;
      }

      setDetails(data);
    })();

    return () => {
      active = false;
    };
  }, [authorizationId]);

  const decide = async (approve: boolean) => {
    const api = oauthApi();
    if (!api) return;

    setBusy(true);
    const { data, error: decisionError } = approve
      ? await api.approveAuthorization(authorizationId)
      : await api.denyAuthorization(authorizationId);

    if (decisionError) {
      setBusy(false);
      setError(decisionError.message);
      return;
    }

    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(false);
      setError("No redirect was returned by the authorization server.");
      return;
    }

    window.location.href = target;
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-background px-4 py-16">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-8 shadow-sm">
        <h1 className="text-2xl font-bold text-foreground mb-2">Connect an app</h1>

        {error && (
          <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 p-4">
            <p className="text-sm text-destructive">
              Could not load this authorization request: {error}
            </p>
          </div>
        )}

        {!error && !details && (
          <p className="text-sm text-muted-foreground mt-4">Loading authorization request…</p>
        )}

        {!error && details && (
          <>
            <p className="text-sm text-muted-foreground mt-4">
              <span className="font-medium text-foreground">
                {details.client?.name ?? "An MCP client"}
              </span>{" "}
              is asking to use the DeepSeek Fan Hub MCP server as you. It will be able to call the
              server's read-only tools — listing blog posts and reading public site metadata.
            </p>
            <p className="text-xs text-muted-foreground mt-3">
              You can revoke this access at any time. This site is an independent fan site and is
              not affiliated with DeepSeek.com.
            </p>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                disabled={busy}
                onClick={() => decide(true)}
                className="flex-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                Approve
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => decide(false)}
                className="flex-1 rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
              >
                Deny
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default OAuthConsent;
